/** Per-employee navbar punch worklog (local fallback when API is slow/offline). */

export const ATTENDANCE_PUNCH_UPDATED_EVENT = "hrms-attendance-punch-updated";

export const LEGACY_CHECKIN_KEY = "adminHomeCheckInAt";
export const LEGACY_WORKLOG_KEY = "adminHomeWorkLogByDay";
export const LEGACY_LAST_SESSION_KEY = "adminHomeLastSession";

export const employeeCheckInKey = (employeeId) =>
  employeeId != null ? `hrms_employee_checkin_${String(employeeId)}` : null;

export const employeeWorklogKey = (employeeId) =>
  employeeId != null ? `hrms_employee_worklog_${String(employeeId)}` : null;

export const employeeLastSessionKey = (employeeId) =>
  employeeId != null ? `hrms_employee_last_session_${String(employeeId)}` : null;

export function getLocalDayKey(ms = Date.now()) {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function readEmployeeWorklog(employeeId, dayKey = getLocalDayKey()) {
  if (!employeeId) return null;
  try {
    const raw = localStorage.getItem(employeeWorklogKey(employeeId));
    if (!raw) {
      if (String(employeeId) === readLegacyEmployeeId()) {
        return readLegacyWorklogRow(dayKey);
      }
      return null;
    }
    const parsed = JSON.parse(raw);
    const row = parsed?.[dayKey];
    return normalizeWorklogRow(row);
  } catch {
    return null;
  }
}

export function readEmployeeWorklogMap(employeeId) {
  if (!employeeId) return {};
  try {
    const raw = localStorage.getItem(employeeWorklogKey(employeeId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function normalizeWorklogRow(row) {
  if (!row || typeof row !== "object") return null;
  const inMs = Number(row.inMs);
  const outMs = row.outMs == null ? null : Number(row.outMs);
  if (!Number.isFinite(inMs) || inMs <= 0) return null;
  if (outMs != null && (!Number.isFinite(outMs) || outMs <= 0)) return null;
  return { inMs, outMs };
}

function readLegacyEmployeeId() {
  try {
    return localStorage.getItem("hrms_current_employee_id") || "";
  } catch {
    return "";
  }
}

function readLegacyWorklogRow(dayKey) {
  try {
    const raw = localStorage.getItem(LEGACY_WORKLOG_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return normalizeWorklogRow(parsed?.[dayKey]);
  } catch {
    return null;
  }
}

export function saveEmployeeWorklogDay(employeeId, inMs, outMs) {
  if (!employeeId || !Number.isFinite(inMs) || inMs <= 0) return;
  const dayKey = getLocalDayKey(inMs);
  try {
    const key = employeeWorklogKey(employeeId);
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : {};
    const next = parsed && typeof parsed === "object" ? parsed : {};
    next[dayKey] = { inMs, outMs: outMs ?? null };
    localStorage.setItem(key, JSON.stringify(next));
    localStorage.setItem("hrms_current_employee_id", String(employeeId));
    if (readLegacyEmployeeId() === String(employeeId) || !localStorage.getItem(LEGACY_WORKLOG_KEY)) {
      const legacyRaw = localStorage.getItem(LEGACY_WORKLOG_KEY);
      const legacy = legacyRaw ? JSON.parse(legacyRaw) : {};
      const legacyNext = legacy && typeof legacy === "object" ? legacy : {};
      legacyNext[dayKey] = { inMs, outMs: outMs ?? null };
      localStorage.setItem(LEGACY_WORKLOG_KEY, JSON.stringify(legacyNext));
    }
  } catch {
    /* ignore */
  }
  dispatchPunchUpdated(employeeId, dayKey);
}

export function dispatchPunchUpdated(employeeId, dayKey) {
  try {
    window.dispatchEvent(
      new CustomEvent(ATTENDANCE_PUNCH_UPDATED_EVENT, {
        detail: { employeeId: String(employeeId), dayKey },
      })
    );
  } catch {
    /* ignore */
  }
}

function persistCheckInFlag(employeeId, checkedIn, checkInAt) {
  const key = employeeId ? employeeCheckInKey(employeeId) : LEGACY_CHECKIN_KEY;
  try {
    if (checkedIn && checkInAt != null) {
      localStorage.setItem(key, String(checkInAt));
      if (employeeId) localStorage.setItem(LEGACY_CHECKIN_KEY, String(checkInAt));
    } else {
      localStorage.removeItem(key);
      if (
        !employeeId ||
        employeeId === localStorage.getItem("hrms_current_employee_id")
      ) {
        localStorage.removeItem(LEGACY_CHECKIN_KEY);
      }
    }
  } catch {
    /* ignore */
  }
}

function persistLastSession(employeeId, session) {
  const key = employeeId ? employeeLastSessionKey(employeeId) : LEGACY_LAST_SESSION_KEY;
  try {
    localStorage.setItem(key, JSON.stringify(session));
    if (employeeId) {
      localStorage.setItem(LEGACY_LAST_SESSION_KEY, JSON.stringify(session));
    }
  } catch {
    /* ignore */
  }
}

/** Same local punch path as navbar check-in (no API). */
export function recordAttendanceCheckIn(employeeId, atMs = Date.now()) {
  if (!employeeId) return null;
  const id = String(employeeId);
  persistCheckInFlag(id, true, atMs);
  saveEmployeeWorklogDay(id, atMs, null);
  return { employeeId: id, inMs: atMs, outMs: null };
}

/** Same local punch path as navbar check-out (no API). */
export function recordAttendanceCheckOut(employeeId, atMs = Date.now()) {
  if (!employeeId) return null;
  const id = String(employeeId);
  const today = readEmployeeWorklog(id, getLocalDayKey(atMs));
  const inMs = today?.inMs ?? atMs;
  saveEmployeeWorklogDay(id, inMs, atMs);
  persistCheckInFlag(id, false, null);
  persistLastSession(id, { inMs, outMs: atMs });
  return { employeeId: id, inMs, outMs: atMs };
}
